import {
  ArrowsLeftRightIcon,
  BookOpenIcon,
  BrainIcon,
  CodeIcon,
  GlobeIcon,
  PlugsConnectedIcon,
  RobotIcon,
  SparkleIcon,
  TreeStructureIcon,
} from "@phosphor-icons/react/dist/ssr";
import type { ComponentType } from "react";

/**
 * Data model for the Getting Started wizard.
 *
 * The wizard is three phases: pick a build, pick a method, follow the tutorial.
 * Phases 1 and 2 are custom pages driven by the `BUILDS` and `METHODS` tables
 * below. Phase 3 is a regular MDX page per build/method pair so the tutorial
 * content stays server-rendered, indexed by search, and published to
 * `llms.txt` — see `docs/getting-started/<build>/<method>.mdx`.
 *
 * `TUTORIALS` is the bridge between the two: it maps a pair to the page that
 * implements it, and records whether that page is still a placeholder.
 */

type PhosphorIcon = ComponentType<{ size?: number; weight?: "duotone" }>;

export interface Build {
  id: string;
  /** Available tutorial methods. Omit when all methods are supported. */
  methods?: string[];
  icon: PhosphorIcon;
  label: string;
  desc: string;
  summary: string;
  color: string;
  time: string;
  /** Highlights one tutorial in the build picker. */
  featured?: boolean;
  /** Outcomes shared by the available workflows. */
  youll: string[];
}

export interface Method {
  id: string;
  icon: PhosphorIcon;
  label: string;
  desc: string;
  /** Workflow requirements; tutorials list additional product prerequisites. */
  needs: string;
  /** The method card's call to action, in full. */
  cta: string;
}

export const BUILDS: Build[] = [
  {
    id: "ai-gateway",
    methods: ["portal"],
    icon: BrainIcon,
    label: "AI Gateway",
    desc: "Connect your app to AI models through a gateway with API key authentication and usage controls.",
    summary:
      "Create an AI Gateway app and send a chat request using the free demo provider.",
    color: "bg-violet-500/10 text-violet-700 dark:text-violet-300",
    time: "~20 min",
    featured: true,
    youll: [
      "Create an app with API key authentication",
      "Test a chat request in the Playground",
      "Connect with cURL and the OpenAI SDK",
    ],
  },
  {
    id: "mcp-gateway",
    icon: TreeStructureIcon,
    label: "MCP Gateway",
    desc: "Connect agents to existing MCP servers through a gateway that controls access to their tools.",
    summary:
      "Connect an MCP client to Linear through your own gateway and call a Linear tool.",
    color: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300",
    time: "~20 min",
    youll: [
      "Add Linear as an upstream MCP server",
      "Configure sign-in and connect your Linear account",
      "Call a tool through the gateway",
    ],
  },
  {
    id: "mcp-server",
    icon: RobotIcon,
    label: "MCP Server",
    desc: "Turn an API into MCP tools so AI assistants can discover its operations and make requests.",
    summary:
      "Expose a sample Todo API as an MCP server and test its tools with an MCP client.",
    color: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    time: "~15 min",
    youll: [
      "Create a project with a sample API",
      "Choose which API operations become tools",
      "Connect an MCP client and call a tool",
    ],
  },
  {
    id: "gateway",
    icon: PlugsConnectedIcon,
    label: "API Gateway",
    desc: "Put a gateway in front of a REST API, then add authentication, rate limits, and Git-based deployment.",
    summary:
      "Build an API gateway that forwards requests to a sample backend and applies policies to each request.",
    color: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
    time: "~45 min",
    youll: [
      "Create and test a route to a sample API",
      "Add API key authentication and rate limits",
      "Deploy from GitHub and set limits per consumer",
    ],
  },
  {
    id: "dev-portal",
    icon: BookOpenIcon,
    label: "Developer Portal",
    desc: "Give developers interactive API documentation and a place to sign in and manage their API keys.",
    summary:
      "Create a developer portal for a sample API, then sign in and test a request with an assigned API key.",
    color: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
    time: "~15 min",
    youll: [
      "Publish API reference docs from OpenAPI",
      "Assign an API key to a consumer",
      "Sign in and send an authenticated request",
    ],
  },
  {
    id: "migrate",
    icon: ArrowsLeftRightIcon,
    label: "Migrate an existing API",
    desc: "Import your OpenAPI document and test Zuplo in front of your backend before moving client traffic.",
    summary:
      "Import an API into Zuplo and verify that a request through the gateway matches the original backend.",
    color: "bg-orange-500/10 text-orange-700 dark:text-orange-300",
    time: "~30 min",
    youll: [
      "Import your API or the Petstore example",
      "Configure a route to your backend",
      "Compare a read-only request through both endpoints",
    ],
  },
];

export const METHODS: Method[] = [
  {
    id: "portal",
    icon: GlobeIcon,
    label: "Zuplo Portal",
    desc: "Configure your project in the browser with the visual Route Designer and built-in testing tools.",
    needs: "a browser and a free Zuplo account",
    cta: "Use the Portal",
  },
  {
    id: "local",
    icon: CodeIcon,
    label: "Local with an editor",
    desc: "Create a project with the Zuplo CLI, edit its files, and test changes on your machine.",
    needs: "Node.js 24+, a terminal, and your editor",
    cta: "Use an editor",
  },
  {
    id: "agent",
    icon: SparkleIcon,
    label: "Local with an AI agent",
    desc: "Use a guided prompt with Claude Code, Cursor, or Codex, then review and test the changes.",
    needs: "Node.js 24+ and an AI coding agent",
    cta: "Use an AI agent",
  },
];

/**
 * A tutorial's steps, when it is long enough to be worth splitting across
 * pages instead of stacking on one.
 *
 * A pair absent from this table is one page, which is the right shape for a
 * fifteen-minute tutorial. The gateway paths run to roughly 700 rendered lines
 * each, which is a scroll no reader keeps their place in, so those get a page
 * per step, each one listed in the sidebar.
 *
 * Titles live here rather than in the MDX because every step page has to draw
 * the whole list, not just its own entry.
 */
export interface TutorialStepRef {
  /** URL segment under the method, e.g. "add-rate-limiting". */
  slug: string;
  /** Sidebar row and page heading. */
  title: string;
  description: string;
  prerequisite?: string;
  prerequisiteStep?: string;
}

const GATEWAY_STEPS: TutorialStepRef[] = [
  {
    slug: "setup-a-basic-gateway",
    title: "Set up a basic gateway",
    description:
      "Create a project and test a request through Zuplo to a sample backend. This is the starting point for the API Gateway tutorial.",
  },
  {
    slug: "add-rate-limiting",
    title: "Add rate limiting",
    description:
      "Limit requests from each IP address, then test that requests over the limit return a 429 response.",
    prerequisite: "Start with a working route from the gateway setup step.",
    prerequisiteStep: "setup-a-basic-gateway",
  },
  {
    slug: "add-api-key-auth",
    title: "Add API key authentication",
    description:
      "Require an API key on your route and test that requests without a valid key return a 401 response.",
    prerequisite:
      "Use the project and route you created in the gateway setup step.",
    prerequisiteStep: "setup-a-basic-gateway",
  },
  {
    slug: "deploy-to-the-edge",
    title: "Deploy to the edge",
    description:
      "Connect your project to GitHub so changes in your repository deploy to a hosted gateway.",
    prerequisite:
      "You need a Zuplo project, a GitHub account, and access to the repository you want to connect.",
    prerequisiteStep: "setup-a-basic-gateway",
  },
  {
    slug: "dynamic-rate-limiting",
    title: "Dynamic rate limiting",
    description:
      "Use consumer metadata to give free and premium customers different request limits.",
    prerequisite:
      "Your route needs rate limiting and API key authentication before you add limits per consumer.",
    prerequisiteStep: "add-api-key-auth",
  },
];

export const TUTORIAL_STEPS: Record<string, TutorialStepRef[]> = {
  "gateway:portal": GATEWAY_STEPS,
  "gateway:local": GATEWAY_STEPS,
};

export const getBuild = (id: string) => BUILDS.find((b) => b.id === id);
export const getMethod = (id: string) => METHODS.find((m) => m.id === id);
export const buildMethods = (buildId: string) => {
  const supported = getBuild(buildId)?.methods;
  return METHODS.filter(
    (method) => !supported || supported.includes(method.id),
  );
};

/** The steps of a split tutorial, or undefined when it is a single page. */
export const tutorialSteps = (buildId: string, methodId: string) =>
  TUTORIAL_STEPS[`${buildId}:${methodId}`];

/**
 * The method that owns a step's bare URL.
 *
 * A step is one page per method, and the method is the last segment rather
 * than the first so that every method of a step sits under one path. That is
 * what lets a single sidebar row stay marked whichever method you are reading:
 * Zudoku matches a category's link by prefix, and only this shape gives the
 * three pages a common one.
 */
const DEFAULT_METHOD = METHODS[0]!.id;

/**
 * Where a method's card and tab point. A split tutorial has no page of its
 * own — its first step is the tutorial — so the link goes straight there
 * rather than through a landing page that would only list what the sidebar
 * already shows.
 */
/** A single step page of a split tutorial. */
export const stepHref = (buildId: string, methodId: string, slug: string) => {
  const base = `/getting-started/${buildId}/${slug}`;
  return methodId === DEFAULT_METHOD ? base : `${base}/${methodId}`;
};

/** Where a step's sidebar row points, and what every method of it sits under. */
export const stepBaseHref = (buildId: string, slug: string) =>
  `/getting-started/${buildId}/${slug}`;

/**
 * Where a method's card and tab point. A split tutorial has no page of its
 * own — its first step is the tutorial — so the link goes straight there
 * rather than through a landing page that would only list what the sidebar
 * already shows.
 */
export const tutorialHref = (buildId: string, methodId: string) => {
  const steps = tutorialSteps(buildId, methodId);
  return steps
    ? stepHref(buildId, methodId, steps[0]!.slug)
    : `/getting-started/${buildId}/${methodId}`;
};

export const buildHref = (buildId: string) => {
  const methods = buildMethods(buildId);
  return methods.length === 1
    ? tutorialHref(buildId, methods[0]!.id)
    : `/getting-started/${buildId}`;
};

/**
 * Start loading a tutorial page's chunk without navigating to it.
 *
 * Zudoku's docs plugin already code-splits every `.mdx` page, and a tutorial
 * page is an expensive chunk: each one pulls in the five articles it stacks,
 * plus their images and syntax-highlighting languages. Left to the router that
 * work starts on click, and the reader watches the old page for about a second
 * before it swaps. Importing the same modules here gives us a handle on those
 * chunks so the fetch can start on hover instead — Vite keys its module graph
 * by resolved id, so these are the very same chunks the router loads.
 *
 * The glob is resolved on first use rather than at module scope.
 * `import.meta.glob` is a Vite compile-time transform, and this module is also
 * loaded by scripts/generate-agentic-discovery.ts (via zudoku.config.tsx ->
 * src/components.tsx) under plain Node, where the untransformed call throws.
 * Deferring it means that process imports the module without ever running the
 * glob, and the catch keeps a speculative prefetch from ever being fatal.
 *
 * Safe to call repeatedly — Vite caches the module promise, so repeated hovers
 * on the same card cost nothing. Failures are swallowed on purpose: the
 * navigation that follows surfaces any real error through the router.
 */
type TutorialModules = Record<string, () => Promise<unknown>>;

let tutorialModules: TutorialModules | undefined;

const getTutorialModules = (): TutorialModules => {
  if (!tutorialModules) {
    try {
      tutorialModules = import.meta.glob(
        "../../../docs/getting-started/**/*.mdx",
      );
    } catch {
      tutorialModules = {};
    }
  }
  return tutorialModules;
};

export const prefetchTutorial = (buildId: string, methodId: string) => {
  const steps = tutorialSteps(buildId, methodId);
  const file = steps
    ? `${buildId}/${steps[0]!.slug}/${
        methodId === DEFAULT_METHOD ? "index" : methodId
      }`
    : `${buildId}/${methodId}`;
  const load =
    getTutorialModules()[`../../../docs/getting-started/${file}.mdx`];

  void load?.().catch(() => {});
};
